/**
 * Runtime configuration service.
 * Fetches configuration from the API at startup instead of using build-time environment variables.
 * This allows a single build artifact to be deployed to multiple environments.
 *
 * Configuration flow:
 * 1. Load /config.json (static file replaced during deployment) to get API base URL
 * 2. Fetch full config from API's /ClientConfig endpoint
 * 3. Fall back to build-time env vars if both fail (never to localhost in deployed builds)
 */

import configEndpoints from './endpoints/config.endpoints';

let config = null;
let configPromise = null;

/**
 * Production web hostnames (CORS / public web — see SafePathMainApi appsettings.PROD).
 * If public/config.json is not replaced at deploy and still points at a non-prod API, users on
 * these hosts would otherwise get the wrong Okta client id from ClientConfig.
 */
const PROD_WEB_HOSTS = new Set([
  'app.safepathsupervision.com',
  'usadmt-prod-safepath-web-app.azurewebsites.net',
]);

/** Keep in sync with ClientConfig:ApiBaseUrl in SafePathMainApi/appsettings.PROD.json */
const PROD_MAIN_API_BASE_URL = 'https://usadmt-prod-safepath-api-app.azurewebsites.net';

/**
 * Build-time API base URL from .env — used as a safe fallback when runtime config fails.
 * In deployed environments this is always the correct URL for the target environment.
 * Only resolves to localhost during local development (when .env has localhost).
 */
const BUILD_TIME_API_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5249';
const BUILD_TIME_OKTA_REDIRECT = process.env.REACT_APP_OKTA_REDIRECT_URI || `${window.location.origin}/login/callback`;

/**
 * Default configuration used when runtime config cannot be loaded.
 * Uses build-time environment variables so deployed builds never fall back to localhost.
 */
const getDefaultConfig = () => ({
  apiBaseUrl: BUILD_TIME_API_URL,
  authServiceUrl: process.env.REACT_APP_API_AUTH_URL || '',
  videoServiceUrl: process.env.REACT_APP_VIDEO_SERVICE_URL || '',
  geospatialApiUrl: process.env.REACT_APP_GEOSPATIAL_API_URL || '',
  ocpApimSubscriptionKey: process.env.REACT_APP_OCP_APIM_SUBSCRIPTION_KEY || '',
  oktaClientId: '0oajoykvouvxQ0CT34h7',
  oktaIssuer: 'https://bankersfinancialcorp.okta.com/oauth2/default',
  oktaRedirectUri: BUILD_TIME_OKTA_REDIRECT,
  logRocketAppId: '',
  sentryDsn: '',
  sentryEnv: 'development',
  // Store destinations for the public /mobileapp landing page. Safe defaults if the
  // API is unreachable; real values come from the /ClientConfig endpoint at runtime.
  iosAppStoreUrl: process.env.REACT_APP_IOS_APP_STORE_URL || '',
  androidAppStoreUrl: process.env.REACT_APP_ANDROID_APP_STORE_URL || '',
  // Publicly reachable URL of the /mobileapp page itself. Used by the QR code
  // so a phone scan lands on a real public host rather than localhost.
  smartDownloadLink: process.env.REACT_APP_SMART_DOWNLOAD_LINK || '',
});

/**
 * Loads the bootstrap config from /config.json to get the API base URL.
 * This file is replaced during deployment for each environment.
 * @returns {Promise<string>} The API base URL
 */
const loadBootstrapConfig = async () => {
  if (typeof window !== 'undefined' && PROD_WEB_HOSTS.has(window.location.hostname)) {
    return PROD_MAIN_API_BASE_URL;
  }

  try {
    const response = await fetch('/config.json', {
      cache: 'no-cache', // Ensure we get fresh config
    });

    if (!response.ok) {
      throw new Error(`Failed to load config.json: ${response.status}`);
    }

    const data = await response.json();
    return data.apiBaseUrl || BUILD_TIME_API_URL;
  } catch (error) {
    console.warn('Failed to load /config.json, falling back to build-time URL:', error.message);
    return BUILD_TIME_API_URL;
  }
};

/**
 * Fetches full configuration from the API.
 * @returns {Promise<Object>} The configuration object
 */
const fetchConfig = async () => {
  const baseUrl = await loadBootstrapConfig();

  try {
    const response = await fetch(configEndpoints.getClientConfigUrl(baseUrl), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Config fetch failed: ${response.status}`);
    }

    const data = await response.json();

    return {
      apiBaseUrl: data.apiBaseUrl || baseUrl,
      authServiceUrl: data.authServiceUrl || '',
      videoServiceUrl: data.videoServiceUrl || '',
      geospatialApiUrl: data.geospatialApiUrl || '',
      ocpApimSubscriptionKey: data.ocpApimSubscriptionKey || '',
      oktaClientId: data.oktaClientId || '0oajoykvouvxQ0CT34h7',
      oktaIssuer: data.oktaIssuer || 'https://bankersfinancialcorp.okta.com/oauth2/default',
      oktaRedirectUri: data.oktaRedirectUri || BUILD_TIME_OKTA_REDIRECT,
      logRocketAppId: data.logRocketAppId || '',
      sentryDsn: data.sentryDsn || '',
      sentryEnv: data.sentryEnvironment || 'development',
      iosAppStoreUrl: data.iosAppStoreUrl || process.env.REACT_APP_IOS_APP_STORE_URL || '',
      androidAppStoreUrl: data.androidAppStoreUrl || process.env.REACT_APP_ANDROID_APP_STORE_URL || '',
      smartDownloadLink: data.smartDownloadLink || process.env.REACT_APP_SMART_DOWNLOAD_LINK || '',
    };
  } catch (error) {
    console.warn('Failed to fetch runtime config from API, using build-time defaults:', error.message);
    return getDefaultConfig();
  }
};

/**
 * Initializes the configuration service.
 * Call this once at app startup before rendering.
 * @returns {Promise<Object>} The configuration object
 */
export const initConfig = async () => {
  if (config) {
    return config;
  }

  if (!configPromise) {
    configPromise = fetchConfig().then((fetchedConfig) => {
      config = fetchedConfig;
      return config;
    });
  }

  return configPromise;
};

/**
 * Gets the current configuration synchronously.
 * Returns default config if initConfig hasn't completed yet.
 * @returns {Object} The configuration object
 */
export const getConfig = () => {
  if (!config) {
    console.warn('Config not initialized, returning defaults. Call initConfig() first.');
    return getDefaultConfig();
  }
  return config;
};

/**
 * Gets a specific config value.
 * @param {string} key - The config key
 * @returns {string} The config value
 */
export const getConfigValue = (key) => {
  const cfg = getConfig();
  return cfg[key] || '';
};

export default {
  initConfig,
  getConfig,
  getConfigValue,
};
